import { useRef, useState } from 'react';

import type { DocumentAnswers as NoteAnswers } from '@/shared/api/protocol-ts/model/dto_scribe_encounter_pb';

import { type AnswerEdit } from '@/features/audioRecording';

import type { DocumentHistory } from '../config';
import {
  EMPTY_NOTE_HISTORY,
  findNoteHistoryEntry,
  findNoteUndoEntry,
  getUndoableNoteOperation,
  invertNoteOperation,
  markNoteOperationStored,
  moveNoteHistoryEntry,
  recordNoteOperation,
  toNoteEdit,
  type NoteHistory,
  type NoteOperation,
} from '../lib';

const dropEntry = (history: NoteHistory, entryId: string): NoteHistory => ({
  undo: history.undo.filter((entry) => entry.id !== entryId),
  redo: history.redo.filter((entry) => entry.id !== entryId),
});

// Sends block-note operations and keeps the clinician's one undo and redo of
// the whole note: undo sends the inverse of the latest step, whichever
// sections it changed, and redo repeats what undo reverted. Undo and redo read
// the latest steps, so one sent just before them, as typed text committed
// by the undo itself, is the step they act on.
export const useNoteHistory = ({
  answers,
  onEdit,
}: {
  answers: NoteAnswers | undefined;
  onEdit: (edit: AnswerEdit) => void;
}): DocumentHistory & {
  perform: (operation: NoteOperation, undoable?: boolean) => void;
} => {
  const [history, setHistoryState] = useState(EMPTY_NOTE_HISTORY);
  const latest = useRef(history);

  const setHistory = (update: (current: NoteHistory) => NoteHistory) => {
    latest.current = update(latest.current);
    setHistoryState(latest.current);
  };

  const send = (operation: NoteOperation, entryId?: string) =>
    onEdit(
      toNoteEdit(operation, (stored) => {
        const after = stored.note;
        if (entryId && after) {
          setHistory((current) =>
            markNoteOperationStored(current, entryId, after),
          );
        }
      }),
    );

  const perform = (operation: NoteOperation, undoable = true) => {
    if (!answers || !undoable) {
      send(operation);
      return;
    }
    const id = crypto.randomUUID();
    setHistory((current) =>
      recordNoteOperation(current, { id, operation, before: answers }),
    );
    send(operation, id);
  };

  const undo = () => {
    const entry = findNoteUndoEntry(latest.current.undo);
    if (!entry) {
      return;
    }
    // Typed text not stored yet is undone against the answers shown.
    const after = entry.after ?? answers ?? entry.before;
    const undone = {
      ...entry,
      operation: getUndoableNoteOperation(entry, answers ?? after),
    };
    const inverse = invertNoteOperation(undone.operation, entry.before, after);
    if (!inverse) {
      setHistory((current) => dropEntry(current, entry.id));
      return;
    }
    setHistory((current) =>
      moveNoteHistoryEntry(current, entry.id, 'redo', undone),
    );
    send(inverse);
  };

  const redo = () => {
    const entry = findNoteHistoryEntry(latest.current.redo);
    if (!entry || !answers) {
      return;
    }
    const id = crypto.randomUUID();
    setHistory((current) =>
      moveNoteHistoryEntry(current, entry.id, 'undo', {
        id,
        operation: entry.operation,
        before: answers,
      }),
    );
    send(entry.operation, id);
  };

  return {
    perform,
    undo,
    redo,
    canUndo: !!findNoteUndoEntry(history.undo),
    canRedo: !!findNoteHistoryEntry(history.redo),
  };
};
